import { createApiRef } from '@backstage/core-plugin-api';

/**
 * Auth0 credentials returned from the backend
 */
export interface Auth0Credentials {
  appName: string;
  environment: string;
  clientId: string;
  domain: string;
  apiIdentifier: string;
  /** The Key Vault secret name for the client secret */
  secretName: string;
}

/**
 * Credentials of an application in one Auth0 tenant it was onboarded to.
 * `found` is false when Key Vault holds no credentials for that tenant.
 * `status` says why: `waiting` while the tenant's onboarding is still open
 * (its build runs, waits for an approval, or is being finished), `failed`
 * with a short `reason` when its last onboarding failed, and `missing` when
 * no finished onboarding is recorded. A `found` tenant has a `reason` when
 * its latest onboarding failed and the credentials are from an earlier one.
 */
export interface TenantCredentials extends Auth0Credentials {
  tenant: string;
  found: boolean;
  status: 'found' | 'waiting' | 'failed' | 'missing';
  reason?: string;
}

/**
 * Credentials of an application in every Auth0 tenant of its deployment
 * environments. `auth0Enabled` is false when the application has no Auth0.
 */
export interface ApplicationCredentials {
  appName: string;
  auth0Enabled: boolean;
  tenants: TenantCredentials[];
}

/**
 * Summary of a created app, as returned by GET /apps. Powers the Modify
 * Application list page.
 */
export interface AppSummary {
  app_name: string;
  app_type: string;
  frontend: string;
  backend: string;
  integrations: string;
  requested_by: string | null;
  repo_url: string | null;
  // The last state known to be MERGED into infra-live. The Modify wizard
  // prefills from this, so it never describes infrastructure that does not exist.
  form_json: string | null;
  // A Day-2 edit whose infra PR(s) are still open — i.e. requested but NOT yet
  // applied to Azure. Promoted into form_json once the PRs merge.
  pending_form_json: string | null;
  /** JSON array of the open infra-live pull-request ids. */
  pending_pr_ids: string | null;
  pending_created_at: string | null;
  // When discovery last saw this app in application-iac-infra-live. Null means it
  // has no infrastructure — a Day-1 record whose PR was never merged — so there
  // is nothing to modify and it must not be listed.
  infra_seen_at: string | null;
  created_at: string;
}

/**
 * API for interacting with the Auth0 Onboarding backend
 */
export interface Auth0OnboardingApi {
  /**
   * Get credentials for an application in a specific Auth0 tenant
   */
  getCredentials(
    appName: string,
    environment: string,
  ): Promise<Auth0Credentials>;

  /**
   * Get credentials for an application in every Auth0 tenant it was
   * onboarded to
   */
  getApplicationCredentials(appName: string): Promise<ApplicationCredentials>;

  /**
   * Reveal the client secret for an application (fetched on demand)
   */
  revealSecret(
    appName: string,
    environment: string,
  ): Promise<string>;

  /**
   * Manually trigger onboarding for an application
   */
  triggerOnboarding(
    appName: string,
  ): Promise<{ status: string; message: string }>;

  /**
   * Get a download URL for the app's starter code ZIP, or undefined when no
   * ZIP is stored for it
   */
  getArtifactUrl(appName: string): Promise<string | undefined>;

  /**
   * List all created apps (Modify Application page).
   */
  listApps(): Promise<AppSummary[]>;

  /**
   * Get the stored scaffolder form snapshot for an app, used to prefill the
   * Day-2 Modify wizard. Returns null when no snapshot was recorded.
   */
  getAppForm(appName: string): Promise<Record<string, unknown> | null>;

  /**
   * Import apps already provisioned in infrastructure so they become
   * modifiable. Reconstructs a form snapshot for each; skips apps that already
   * have one.
   */
  backfillApps(): Promise<BackfillResult>;
}

export interface BackfillResult {
  scannedApps: number;
  imported: number;
  skipped: number;
  errors: string[];
}

/**
 * API reference for the Auth0 Onboarding API
 */
export const auth0OnboardingApiRef = createApiRef<Auth0OnboardingApi>({
  id: 'plugin.auth0-onboarding.api',
});
